import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, insuranceOperationsTopic, liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import {
  InsuranceCutoverChecklistItem,
  InsuranceObservabilityStatus,
  InsurancePilotGoLiveStatus,
  InsurancePilotWaveStatus,
  InsuranceReconciliationWindow,
  InsuranceRunbookItem,
  RegulatedInsurancePolicyListItem,
  SalesRegulatedApiService
} from './sales-regulated-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-insurance-policies',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, CheckboxModule, TagModule, SelectModule, InputNumberModule, AinAmountPipe],
  template: `
    <ain-page [title]="t('sales.regulated.insurance.policiesTitle')" [description]="t('sales.regulated.insurance.policiesDescription')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.regulated.insurance.quotesNav')" severity="secondary" (click)="goQuotes()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selected().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
            </span>
          </div>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="20" [selection]="selected()" (selectionChange)="onSelect($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.regulated.insurance.fields.policyNumber') }}</th>
                <th>{{ t('sales.regulated.insurance.fields.premium') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.policyNumber }}</button></td>
                <td>{{ row.premiumAmount | ainAmount }} {{ row.currencyCode }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
        @if (observability(); as obs) {
          <section class="panel">
            <h3>{{ t('sales.regulated.insurance.hardeningTitle') }}</h3>
            <p>{{ t('sales.regulated.insurance.hardeningDescription') }}</p>
            <div class="stats">
              <article><strong>{{ t('sales.regulated.insurance.hardening.metrics.quotesPerDay') }}</strong><span>{{ obs.metrics.quotesPerDay }}</span></article>
              <article><strong>{{ t('sales.regulated.insurance.hardening.metrics.bindRatio') }}</strong><span>{{ obs.metrics.bindRatio }}</span></article>
              <article><strong>{{ t('sales.regulated.insurance.hardening.metrics.lossRatio') }}</strong><span>{{ obs.metrics.lossRatio }}</span></article>
              <article><strong>{{ t('sales.regulated.insurance.hardening.metrics.openClaimAge') }}</strong><span>{{ obs.metrics.openClaimAgeDays }}</span></article>
            </div>
            <p-table [value]="obs.metrics.sloSnapshots">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('sales.regulated.insurance.hardening.slo.flow') }}</th>
                  <th>{{ t('sales.regulated.insurance.hardening.slo.p95') }}</th>
                  <th>{{ t('sales.regulated.insurance.hardening.slo.target') }}</th>
                  <th>{{ t('sales.regulated.insurance.hardening.slo.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-slo>
                <tr>
                  <td>{{ slo.flow }}</td>
                  <td>{{ slo.p95Ms }}</td>
                  <td>{{ slo.targetMs }}</td>
                  <td>
                    <p-tag
                      [severity]="slo.targetMet ? 'success' : 'danger'"
                      [value]="slo.targetMet ? t('sales.regulated.insurance.hardening.sloMet') : t('sales.regulated.insurance.hardening.sloMissed')" />
                  </td>
                </tr>
              </ng-template>
            </p-table>
            <p>
              {{ t('sales.regulated.insurance.hardening.traceCoverage') }}:
              {{ obs.traceCoverage.quoteStage && obs.traceCoverage.underwritingStage && obs.traceCoverage.issueStage && obs.traceCoverage.collectionStage
                ? t('sales.regulated.insurance.hardening.traceCoverage.complete')
                : t('sales.regulated.insurance.hardening.traceCoverage.partial') }}
            </p>
          </section>
        }
        @if (reconciliation(); as recon) {
          <section class="panel">
            <h3>{{ t('sales.regulated.insurance.hardening.reconciliation') }}</h3>
            <p>{{ recon.windowStart }} - {{ recon.windowEnd }} ({{ recon.overallStatus }})</p>
            <p-table [value]="recon.jobs">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('sales.regulated.insurance.hardening.reconciliation.job') }}</th>
                  <th>{{ t('sales.regulated.insurance.hardening.reconciliation.status') }}</th>
                  <th>{{ t('sales.regulated.insurance.hardening.reconciliation.details') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-job>
                <tr>
                  <td>{{ job.jobCode }}</td>
                  <td>{{ job.status }}</td>
                  <td>{{ job.details }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }
        @if (runbooks().length > 0) {
          <section class="panel">
            <h3>{{ t('sales.regulated.insurance.hardening.runbooks') }}</h3>
            <ul>
              @for (item of runbooks(); track item.code) {
                <li><code>{{ item.code }}</code> — {{ item.title }} ({{ item.path }})</li>
              }
            </ul>
          </section>
        }
        @if (pilotGoLive(); as goLive) {
          <section class="panel">
            <h3>{{ t('sales.regulated.insurance.phase18.title') }}</h3>
            <p>{{ t('sales.regulated.insurance.phase18.description') }}</p>

            <div class="toolbar">
              <p-select
                [options]="dataMigrationOptions"
                optionLabel="label"
                optionValue="value"
                appendTo="body"
                [ngModel]="dataMigrationMode()"
                (ngModelChange)="dataMigrationMode.set($event)" />
              <button pButton type="button" severity="secondary" [label]="t('sales.regulated.insurance.phase18.actions.savePilot')" (click)="savePilotScope()"></button>
              <button pButton type="button" severity="secondary" [label]="t('sales.regulated.insurance.phase18.actions.documentGoLive')" (click)="documentProductionGoLive()"></button>
              <button pButton type="button" severity="secondary" [label]="t('sales.regulated.insurance.phase18.actions.handoverBau')" (click)="completeBauHandover()"></button>
            </div>

            <h4>{{ t('sales.regulated.insurance.phase18.pilotWaves') }}</h4>
            <p-table [value]="pilotWavesDraft()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('sales.regulated.insurance.phase18.columns.wave') }}</th>
                  <th>{{ t('sales.regulated.insurance.phase18.columns.lob') }}</th>
                  <th>{{ t('sales.regulated.insurance.phase18.columns.channel') }}</th>
                  <th>{{ t('sales.regulated.insurance.phase18.columns.enabled') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-wave let-rowIndex="rowIndex">
                <tr>
                  <td>{{ wave.waveCode }}</td>
                  <td>{{ wave.lineOfBusiness }}</td>
                  <td>{{ wave.channel }}</td>
                  <td>
                    <p-checkbox
                      [binary]="true"
                      [ngModel]="wave.enabled"
                      (ngModelChange)="togglePilotWave(rowIndex, $event)" />
                  </td>
                </tr>
              </ng-template>
            </p-table>

            <h4>{{ t('sales.regulated.insurance.phase18.cutoverChecklist') }}</h4>
            <p-table [value]="cutoverDraft()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('sales.regulated.insurance.phase18.columns.task') }}</th>
                  <th>{{ t('sales.regulated.insurance.phase18.columns.completed') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-item let-rowIndex="rowIndex">
                <tr>
                  <td>{{ item.title }}</td>
                  <td>
                    <p-checkbox
                      [binary]="true"
                      [ngModel]="item.completed"
                      (ngModelChange)="toggleCutoverItem(rowIndex, $event)" />
                  </td>
                </tr>
              </ng-template>
            </p-table>
            <div class="toolbar">
              <button pButton type="button" severity="secondary" [label]="t('sales.regulated.insurance.phase18.actions.saveChecklist')" (click)="saveCutoverChecklist()"></button>
            </div>

            <h4>{{ t('sales.regulated.insurance.phase18.hypercare') }}</h4>
            <div class="stats">
              <article><strong>{{ t('sales.regulated.insurance.phase18.hypercareWeek') }}</strong><span>{{ goLive.hypercare.currentWeek }} / {{ goLive.hypercare.durationWeeks }}</span></article>
              <article><strong>{{ t('sales.regulated.insurance.phase18.metrics.regulatoryRate') }}</strong><span>{{ goLive.hypercare.regulatorySubmitSuccessRate }}</span></article>
              <article><strong>{{ t('sales.regulated.insurance.phase18.metrics.conversion') }}</strong><span>{{ goLive.hypercare.conversionRatio }}</span></article>
              <article><strong>{{ t('sales.regulated.insurance.phase18.metrics.lossRatio') }}</strong><span>{{ goLive.hypercare.lossRatio }}</span></article>
            </div>
            <div class="toolbar">
              <p-checkbox
                [binary]="true"
                inputId="war-room-active"
                [ngModel]="warRoomDailyActive()"
                (ngModelChange)="warRoomDailyActive.set($event)" />
              <label for="war-room-active">{{ t('sales.regulated.insurance.phase18.warRoomDaily') }}</label>
              <p-inputNumber
                inputId="open-p1-incidents"
                [min]="0"
                [showButtons]="true"
                [ngModel]="openP1Incidents()"
                (ngModelChange)="onOpenP1IncidentsChange($event)" />
              <label for="open-p1-incidents">{{ t('sales.regulated.insurance.phase18.openP1Incidents') }}</label>
              <button pButton type="button" severity="secondary" [label]="t('sales.regulated.insurance.phase18.actions.saveHypercare')" (click)="saveHypercare()"></button>
            </div>

            <h4>{{ t('sales.regulated.insurance.phase18.goNoGo') }}</h4>
            <ul>
              <li><code>Program DoD</code> <p-tag [severity]="goLive.goNoGoCriteria.programDodSatisfied ? 'success' : 'danger'" [value]="goLive.goNoGoCriteria.programDodSatisfied ? '✅' : '⛔'"></p-tag></li>
              <li><code>P1 4 weeks stable</code> <p-tag [severity]="goLive.goNoGoCriteria.pilotP1StableFourWeeks ? 'success' : 'danger'" [value]="goLive.goNoGoCriteria.pilotP1StableFourWeeks ? '✅' : '⛔'"></p-tag></li>
              <li><code>Regulatory success > 99%</code> <p-tag [severity]="goLive.goNoGoCriteria.regulatorySubmitSuccessRateMet ? 'success' : 'danger'" [value]="goLive.goNoGoCriteria.regulatorySubmitSuccessRateMet ? '✅' : '⛔'"></p-tag></li>
              <li><code>UFC reconciliation zero variance</code> <p-tag [severity]="goLive.goNoGoCriteria.ufcReconciliationZeroMaterialVariance ? 'success' : 'danger'" [value]="goLive.goNoGoCriteria.ufcReconciliationZeroMaterialVariance ? '✅' : '⛔'"></p-tag></li>
            </ul>
          </section>
        }
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesInsurancePoliciesComponent implements OnInit, OnDestroy {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly router = inject(Router);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveTopic: string | null = null;
  private liveEventsSubscription: Subscription | null = null;
  readonly loading = signal(true);
  readonly rows = signal<RegulatedInsurancePolicyListItem[]>([]);
  readonly selected = signal<RegulatedInsurancePolicyListItem[]>([]);
  readonly observability = signal<InsuranceObservabilityStatus | null>(null);
  readonly reconciliation = signal<InsuranceReconciliationWindow | null>(null);
  readonly runbooks = signal<InsuranceRunbookItem[]>([]);
  readonly pilotGoLive = signal<InsurancePilotGoLiveStatus | null>(null);
  readonly pilotWavesDraft = signal<InsurancePilotWaveStatus[]>([]);
  readonly cutoverDraft = signal<InsuranceCutoverChecklistItem[]>([]);
  readonly dataMigrationMode = signal<'new_business_only' | 'full_portfolio_migration'>('new_business_only');
  readonly warRoomDailyActive = signal(true);
  readonly openP1Incidents = signal(0);
  readonly dataMigrationOptions = [
    { label: this.t('sales.regulated.insurance.phase18.dataMigration.newBusiness'), value: 'new_business_only' },
    { label: this.t('sales.regulated.insurance.phase18.dataMigration.fullPortfolio'), value: 'full_portfolio_migration' }
  ];

  ngOnInit(): void {
    this.load();
    void this.startLiveUpdates();
  }
  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    if (this.liveTopic) {
      void this.liveUpdateService.unsubscribe(this.liveTopic);
      this.liveTopic = null;
    }
  }
  load(): void {
    this.loading.set(true);
    this.api.listInsurancePolicies().subscribe({
      next: r => { this.rows.set(r); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
    this.api.getInsuranceObservabilityStatus().subscribe({
      next: value => this.observability.set(value),
      error: () => this.observability.set(null)
    });
    this.api.getInsuranceReconciliationWindow().subscribe({
      next: value => this.reconciliation.set(value),
      error: () => this.reconciliation.set(null)
    });
    this.api.listInsuranceRunbooks().subscribe({
      next: value => this.runbooks.set(value),
      error: () => this.runbooks.set([])
    });
    this.api.getInsurancePilotGoLiveStatus().subscribe({
      next: value => this.applyGoLiveStatus(value),
      error: () => this.pilotGoLive.set(null)
    });
  }
  onSelect(v: RegulatedInsurancePolicyListItem[]): void { this.selected.set(v ?? []); }
  openCreate(): void { void this.router.navigate(['/sales/insurance-policies/new']); }
  openSelected(): void { const id = this.selected()[0]?.id; if (id) this.openDetail(id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/insurance-policies', id]); }
  goQuotes(): void { void this.router.navigate(['/sales/insurance-quotes']); }
  liveStatusTranslationKey(state: 'disconnected' | 'connecting' | 'connected' | 'reconnecting'): string {
    return liveStatusTranslationKey(state);
  }
  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const topic = insuranceOperationsTopic(tenantId);
    this.liveTopic = topic;
    await this.liveUpdateService.subscribe(topic);
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(
        filter(event =>
          event.eventType === LiveUpdateEventTypes.insurancePoliciesUpdated
          || event.eventType === LiveUpdateEventTypes.insuranceQuotesUpdated
          || event.eventType === LiveUpdateEventTypes.insuranceClaimsUpdated
          || event.eventType === LiveUpdateEventTypes.insuranceGoLiveUpdated))
      .subscribe(() => this.load());
  }
  togglePilotWave(index: number, enabled: boolean): void {
    this.pilotWavesDraft.update(items =>
      items.map((item, i) => (i === index ? { ...item, enabled: !!enabled } : item)));
  }
  toggleCutoverItem(index: number, completed: boolean): void {
    this.cutoverDraft.update(items =>
      items.map((item, i) => (i === index ? { ...item, completed: !!completed } : item)));
  }
  onOpenP1IncidentsChange(value: number | null): void {
    this.openP1Incidents.set(Math.max(0, Number(value ?? 0)));
  }
  savePilotScope(): void {
    this.api.updateInsurancePilotScope({
      dataMigrationMode: this.dataMigrationMode(),
      pilotWaves: this.pilotWavesDraft()
    }).subscribe({ next: value => this.applyGoLiveStatus(value) });
  }
  saveCutoverChecklist(): void {
    this.api.updateInsuranceCutoverChecklist({
      items: this.cutoverDraft()
    }).subscribe({ next: value => this.applyGoLiveStatus(value) });
  }
  saveHypercare(): void {
    const current = this.pilotGoLive();
    this.api.updateInsuranceHypercareStatus({
      startedOn: current?.hypercare.startedOn ?? null,
      warRoomDailyActive: this.warRoomDailyActive(),
      openP1Incidents: this.openP1Incidents(),
      bauHandoverCompleted: current?.bauHandoverCompleted ?? false
    }).subscribe({ next: value => this.applyGoLiveStatus(value) });
  }
  documentProductionGoLive(): void {
    this.api.documentInsuranceProductionGoLive()
      .subscribe({ next: value => this.applyGoLiveStatus(value) });
  }
  completeBauHandover(): void {
    this.api.completeInsuranceBauHandover()
      .subscribe({ next: value => this.applyGoLiveStatus(value) });
  }
  private applyGoLiveStatus(value: InsurancePilotGoLiveStatus): void {
    this.pilotGoLive.set(value);
    this.dataMigrationMode.set(value.dataMigrationMode === 'full_portfolio_migration' ? 'full_portfolio_migration' : 'new_business_only');
    this.pilotWavesDraft.set([...(value.pilotWaves ?? [])]);
    this.cutoverDraft.set([...(value.cutoverChecklist ?? [])]);
    this.warRoomDailyActive.set(value.hypercare.warRoomDailyActive);
    this.openP1Incidents.set(value.hypercare.openP1Incidents);
  }
  t(key: string): string { return this.localization.translate(key); }
}
